import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Grid,
    Divider,
} from '@mui/material';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';

const Tools = () => {
    const [tools, setTools] = useState([]);
    const [form, setForm] = useState({
        name: '',
        description: '',
        api_url: '',
        webhook_url: '',
        method: 'POST',
        headers: '{}',
        api_key: '',
        properties: '{}',
    });

    const fetchTools = async () => {
        try {
            const res = await axios.get('/api/vapi/tools');
            setTools(res.data);
        } catch (e) {
            alert('Failed to fetch tools');
        }
    };

    const handleSubmit = async () => {
        try {
            await axios.post('/api/vapi/tools', {
                ...form,
                headers: JSON.parse(form.headers || '{}'),
                properties: JSON.parse(form.properties || '{}'),
            });
            setForm({
                name: '',
                description: '',
                api_url: '',
                webhook_url: '',
                method: 'POST',
                headers: '{}',
                api_key: '',
                properties: '{}',
            });
            fetchTools();
        } catch (e) {
            alert('Invalid JSON in headers or properties.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this tool?')) return;
        try {
            await axios.delete(`/api/vapi/tool/${id}`);
            fetchTools();
        } catch (e) {
            alert('Failed to delete tool');
        }
    };

    useEffect(() => {
        fetchTools();
    }, []);

    return (
        <Box p={3}>
            <Box display="flex" alignItems="center" gap={1} mb={3}>
                <BuildCircleOutlinedIcon color="primary" fontSize="large" />
                <Typography variant="h4" fontWeight="bold">
                    Tools
                </Typography>
            </Box>

            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" mb={2}>
                    Add a Tool
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="API URL"
                            value={form.api_url}
                            onChange={(e) => setForm({ ...form, api_url: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Webhook URL"
                            value={form.webhook_url}
                            onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Method"
                            value={form.method}
                            onChange={(e) => setForm({ ...form, method: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="API Key"
                            value={form.api_key}
                            onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            label="Description"
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            label="Headers (JSON)"
                            value={form.headers}
                            onChange={(e) => setForm({ ...form, headers: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            label="Properties (JSON)"
                            value={form.properties}
                            onChange={(e) => setForm({ ...form, properties: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            sx={{ mt: 1 }}
                        >
                            ➕ Add Tool
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Typography variant="h6" mb={2}>
                Existing Tools
            </Typography>
            <Grid container spacing={2}>
                {tools.map((tool) => (
                    <Grid item xs={12} md={6} key={tool.id}>
                        <Paper elevation={2} sx={{ p: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                {tool.function?.name || tool.name || 'Untitled Tool'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {tool.function?.description || tool.description || 'No description'}
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2">
                                <strong>Type:</strong> {tool.type}
                            </Typography>
                            <Typography variant="body2">
                                <strong>ID:</strong> {tool.id}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Created At:</strong> {tool.createdAt}
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                                <strong>Updated At:</strong> {tool.updatedAt}
                            </Typography>

                            {/* Function Info */}
                            {tool.function && (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2">Function</Typography>
                                    <Typography variant="body2">
                                        <strong>Name:</strong> {tool.function.name}
                                        <br />
                                        <strong>Description:</strong> {tool.function.description}
                                        <br />
                                        <strong>Strict:</strong> {tool.function.strict ? 'Yes' : 'No'}
                                        <br />
                                        <strong>Required Parameters:</strong>{' '}
                                        {tool.function.parameters?.required?.join(', ') || 'None'}
                                    </Typography>
                                </>
                            )}

                            {/* Messages Info */}
                            {tool.messages?.length > 0 && (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2">Messages</Typography>
                                    {tool.messages.map((msg, i) => (
                                        <Box key={i} ml={1} mb={1}>
                                            <Typography variant="body2">
                                                <strong>Type:</strong> {msg.type}
                                                <br />
                                                <strong>Content:</strong> {msg.content}
                                                <br />
                                                <strong>Blocking:</strong> {msg.blocking ? 'Yes' : 'No'}
                                            </Typography>

                                            {/* Conditions */}
                                            {msg.conditions?.length > 0 && (
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Conditions:</strong>{' '}
                                                    {msg.conditions.map((c, j) => (
                                                        <span key={j}>
                                                            [{c.param} {c.operator} {c.value}]
                                                            {j < msg.conditions.length - 1 && ', '}
                                                        </span>
                                                    ))}
                                                </Typography>
                                            )}

                                            {/* Contents */}
                                            {msg.contents?.length > 0 && (
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Contents:</strong>{' '}
                                                    {msg.contents.map((c, j) => (
                                                        <span key={j}>
                                                            ({c.type}: {c.text} [{c.language}])
                                                            {j < msg.contents.length - 1 && ', '}
                                                        </span>
                                                    ))}
                                                </Typography>
                                            )}
                                        </Box>
                                    ))}
                                </>
                            )}

                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                onClick={() => handleDelete(tool.id)}
                                sx={{ mt: 2 }}
                            >
                                🗑 Delete
                            </Button>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default Tools;
